import React from "react";
import { motion } from "framer-motion";

const LOSSES = [
  {
    n: "01",
    title: "Las relaciones que no fueron",
    text: "Personas con las que podías construir algo. Pero el personaje exigía, se cerraba, se conformaba. Hoy sigues sin entender del todo por qué.",
  },
  {
    n: "02",
    title: "El proyecto que abandonaste",
    text: "Esa idea que te encendía. Empezaste, brillaste un mes, y luego apareció el cansancio, el miedo, el «para qué». Sigue ahí, en pausa, llamándote.",
  },
  {
    n: "03",
    title: "El techo que no rompiste",
    text: "El nivel económico que pensabas que llegaría. Las oportunidades que rechazaste por miedo. Los precios que no te atreviste a subir.",
  },
  {
    n: "04",
    title: "La versión de ti que no fuiste",
    text: "Esa persona segura, libre, que sabías que estaba dentro. La que se atrevía a hablar, a pedir, a decir no. La que aún sigues esperando ser.",
  },
  {
    n: "05",
    title: "Los años que se fueron",
    text: "Cinco, diez, quince años repitiendo el mismo guión con caras distintas. Misma relación. Mismo techo. Misma sensación al despertar.",
  },
];

export default function ConsequencesSection() {
  return (
    <section
      id="coste"
      className="section-dark relative py-24 md:py-40 overflow-hidden"
      data-testid="consequences-section"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="bg-grid absolute inset-0 opacity-40" />
        <div className="orb orb-violet orb-drift-1" style={{ width: 560, height: 560, top: "-5%", right: "-10%" }} />
        <div className="orb orb-gold orb-drift-3" style={{ width: 380, height: 380, bottom: "10%", left: "5%" }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl"
        >
          <p className="overline mb-6">◇ El coste del patrón</p>
          <h2 className="font-serif font-normal text-5xl sm:text-6xl lg:text-7xl text-[#F2EFEA] leading-[1.05] tracking-[-0.01em]">
            Lo que <span className="text-[#C7AA78] italic">se quedó</span>
            <br />
            por el camino.
          </h2>
          <div className="gold-divider mt-8" />
          <p className="mt-8 text-lg sm:text-xl text-[#AEB4BC] leading-[1.7] max-w-2xl font-light font-sans">
            Mira hacia atrás. Lo que no se hizo. Lo que no se atrevió. Lo que se conformó.
            Detrás de cada uno de esos momentos había un personaje tomando la decisión por ti.
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {LOSSES.map((l, i) => (
            <motion.div
              key={l.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              className="paper p-7 md:p-8 transition-all duration-300"
              data-testid={`consequence-${i}`}
            >
              <div className="flex items-center justify-between mb-5">
                <span className="font-serif text-[#C7AA78] text-lg italic">{l.n}</span>
                <span className="h-px w-12 bg-[#C7AA78]/30" />
              </div>
              <h3 className="font-serif text-2xl text-[#F2EFEA] mb-3 font-normal tracking-[-0.005em] leading-tight">
                {l.title}
              </h3>
              <p className="text-[#AEB4BC] text-base leading-relaxed font-light font-sans">
                {l.text}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-20 max-w-4xl"
        >
          <p className="font-serif text-3xl sm:text-4xl text-[#F2EFEA] font-normal leading-[1.35] tracking-[-0.005em]">
            &ldquo;Los próximos cinco años se van a parecer mucho a los cinco anteriores.
            <br />
            <span className="text-[#C7AA78] italic">
              No por falta de capacidad. Por lealtad inconsciente al que fuiste.&rdquo;
            </span>
          </p>
          <p className="mt-6 text-sm text-[#7A828C] font-medium tracking-[0.22em] uppercase font-sans">
            — Santi Lorenzo
          </p>
        </motion.div>
      </div>
    </section>
  );
}
